import getBrowserFingerprint from 'get-browser-fingerprint';
(window as any).gbfp = getBrowserFingerprint;
const apiUrl = '/api/v1/sandbox';

export function fakeCreateSandbox(email:string, course:string):Promise<string> {
  return new Promise<string>((resolve, reject) => {
    //setTimeout(() => resolve('https://www.google.com'), 10000);
    setTimeout(() => reject(new Error('Shit happened')), 10000);
  });
}

export function getCloudRedirect(course: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    fetch(`${apiUrl}/redirect`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({course}),
    }).then((response) => { // eslint-disable-line
      if (!response.ok) {
        return response.text()
            .then((err) => {
              throw new Error(err);
            });
      }
      resolve(response.text());
    }).catch((err) => {
      const errmsg = err.message;
      console.error('error received from sandbox API: ', errmsg);
      reject(err);
    });
  });
}

export interface TenantCredentials {
  criblClientId: string;
  criblClientSecret: string;
  criblOrgId: string;
  criblWorkspaceId: string;
  environment: 'staging' | 'production';
}

export function tenantAppUrl(orgId: string, workspaceId: string, environment: 'staging' | 'production'): string {
  const domain = environment === 'production' ? 'cribl.cloud' : 'cribl-staging.cloud';
  return `https://${workspaceId}-${orgId}.${domain}`;
}

// Hard ceiling on total provisioning time, strictly under the 10 minute SLA.
const HARD_TIMEOUT_MS = 9 * 60 * 1000;
const POLL_INTERVAL_MS = 10_000;
const FETCH_TIMEOUT_MS = 15_000;
const MAX_CREATE_ATTEMPTS = 2;
const CREATE_FAILURE_MESSAGE = 'Failed to start the demo. Please try again or contact sandbox@cribl.io if it recurs.';

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// AbortSignal.timeout isn't implemented by jsdom's AbortSignal; fall back to AbortController + setTimeout.
const abortSignalTimeout = (ms: number): AbortSignal => {
  if (typeof (AbortSignal as any).timeout === 'function') {
    return (AbortSignal as any).timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
};

// Every request gets its own timeout so a single hung call can't stall the flow indefinitely.
const timedFetch = (url: string, options: RequestInit): Promise<Response> =>
  fetch(url, {...options, signal: abortSignalTimeout(FETCH_TIMEOUT_MS)});

export function createSandbox(email:string, course:string, maxStartupTime: number, verificationCode:string, ttl:string, refHost:string, tenantCredentials?: TenantCredentials):Promise<string> {
  const startTime = Date.now();
  const deadline = startTime + Math.min(maxStartupTime * 1000, HARD_TIMEOUT_MS);

  const logActivity = (state: string, detail?: string) => {
    const timeSinceStart = Math.floor((Date.now() - startTime)/1000);
    timedFetch(`${apiUrl}/log`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        course,
        maxStartupTime,
        ttl,
        timeSinceStart,
        state,
        ...(detail ? {detail} : {}),
      }),
    }).catch(() => {});
  };

  const body: any = {
    email,
    course,
    verificationCode,
    ttl,
    ...(tenantCredentials || {})
  };
  body.orig_url = `${window.location.protocol}//${window.location.host}`;
  const bodyJson = JSON.stringify(body);

  const teardown = () => {
    timedFetch(`${apiUrl}/terminate`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'xreferredfrom': refHost,
      },
      body: bodyJson,
    }).catch(() => {});
  };

  const attemptCreate = async (): Promise<void> => {
    let lastErrDetail = 'Failed to create sandbox';
    for (let attempt = 0; attempt < MAX_CREATE_ATTEMPTS && Date.now() < deadline; attempt++) {
      logActivity('create');
      let response: Response;
      try {
        response = await timedFetch(`${apiUrl}/create`, {
          method: 'POST',
          mode: 'cors',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'xreferredfrom': refHost,
          },
          body: bodyJson,
        });
      } catch (err: any) {
        lastErrDetail = err.message;
        continue;
      }
      if (response.ok) return;
      const errText = await response.text();
      if (errText.match(/already has a running sandbox/)) return;
      if (response.status >= 400 && response.status < 500) {
        logActivity('create-failed', errText);
        throw new Error(CREATE_FAILURE_MESSAGE);
      }
      lastErrDetail = errText;
    }
    logActivity('create-failed', lastErrDetail);
    throw new Error(CREATE_FAILURE_MESSAGE);
  };

  const pollStatus = async (): Promise<string | null> => {
    while (Date.now() < deadline) {
      logActivity('status');
      try {
        const response = await timedFetch(`${apiUrl}/status`, {
          method: 'POST',
          mode: 'cors',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'xreferredfrom': refHost,
          },
          body: bodyJson,
        });
        if (response.status === 200) {
          const text = await response.text();
          logActivity('complete');
          return text;
        }
      } catch (err) {
        // transient/hung request - fall through and retry until the deadline
      }
      await sleep(POLL_INTERVAL_MS);
    }
    return null;
  };

  return (async () => {
    await attemptCreate();
    const result = await pollStatus();
    if (result === null) {
      logActivity('timeout');
      teardown();
      const minutes = Math.round((deadline - startTime) / 60000);
      throw new Error(`Demo failed to become healthy within ${minutes} minutes and was torn down. Please try again, or contact sandbox@cribl.io if it recurs.`);
    }
    return result;
  })();
};
